import { Fragment, memo, ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { Box, Grid, GridSize } from '@breathelife/mui';
import { Language } from '@breathelife/types';
import {
  AddressAutocompleteInput,
  DatePicker,
  Icon,
  InfoSupplement,
  InputVariant,
  MoneyInput,
  NumberInput,
  PhoneInput as PhoneField,
  SearchableSelect as SearchableSelectField,
  SelectMUI as SelectField,
  TextInput as TextField,
  YearMonthField,
} from '@breathelife/ui-components';
import {
  FieldSize,
  FieldTypes,
  IconName,
  IconNames,
  isRenderingAgreeField,
  isRenderingButtonField,
  isRenderingCheckboxGroupField,
  isRenderingCustomField,
  isRenderingOptionField,
  isRenderingPlaceholderField,
  isRenderingRadioField,
  isRenderingTextField,
  isTextFieldType,
  OnAnswerChange,
  OptionWidth,
  RenderingAutocompleteField,
  RenderingField,
  RenderingNumberField,
  RenderingOptionField,
} from '@zinnia/form-engine-sdk';

import { getFieldSize, getVisibleOptions, shouldForceNewLine } from '../../Helpers/field';
import { getInputVariant } from '../../Helpers/inputVariant';
import { translate } from '../../Localization/Localizer';
import { StyleVariant } from '../FieldGenerator/FieldGenerator';
import { Agree as AgreeField } from './Agree/Agree';
import { CheckboxAgree } from './Agree/CheckboxAgree';
import { ActionButton } from './Button/ActionButton';
import { CheckboxField } from './Checkbox/Checkbox';
import { CheckboxGroup } from './Checkbox/CheckboxGroup';
import { ParticipantID } from './CustomFields/ParticipantID';
import { RadioGroup as RadioGroupField } from './RadioGroup/RadioGroup';
import { SpacerField } from './SpacerField';

const INFO_ICON_BUTTON_SIZE = 1;

const CenteredAlignedGrid = styled(Grid)`
  align-self: center;
`;

const FieldGrid = styled(Grid)`
  * :not(.MuiFormControlLabel-label) {
    min-width: fit-content;
  }

  .MuiFormControlLabel-asterisk {
    display: none;
  }
`;

type FieldProps = {
  field: RenderingField;
  onAnswerChange: OnAnswerChange;
  onAnswerComplete: (fieldId: string, answer: any, previousAnswer: any) => void;
  styleVariant: StyleVariant;
  locale?: Language;
  iconMap?: Record<string, string>;
  onInfoIconClick?: () => void;
  onError?: (fieldId: string, error?: string) => void;
  focusedIncompleteFieldId?: string;
};

export function InnerField(props: FieldProps): ReactElement | null {
  const {
    field,
    onAnswerChange,
    onAnswerComplete: onAnswerCompleteCallback,
    onError,
    styleVariant,
    locale,
    iconMap,
    onInfoIconClick,
    focusedIncompleteFieldId,
  } = props;

  const onAnswerComplete = useCallback(
    (fieldId: string, answer: any, previousAnswer: any) => {
      onAnswerCompleteCallback(fieldId, answer, previousAnswer);
    },
    [onAnswerCompleteCallback],
  );

  const onAnswerChangeForFieldProps = useCallback(
    (answer: any, triggerStepNavigation = field.triggerStepNavigation) => {
      // DOUBLE EQUALITY IS DESIRED HERE. THE INPUT SHOULD IDEAL GIVE NUMBERS WHEN THEY ARE MONEY FIELD BUT THEY ARE SENDING STRING WHICH ARE LATER CONVERTED BY THE ENGINE.
      if (field.value != answer) {
        // Only call the onChange if the value is different from what we have already.
        onAnswerChange([
          {
            tag: 'blueprintId',
            blueprintId: field.blueprintId,
            nodeId: field.nodeId,
            value: answer,
            effects: field.effects,
            blueprintIdScope: field.scope.repeatedInstanceIdentifierContext.byBlueprintId,
            nodeIdScope: field.scope.repeatedInstanceIdentifierContext.byNodeId,
            triggerStepNavigation: triggerStepNavigation,
          },
        ]);
      }
    },
    [
      field.triggerStepNavigation,
      field.value,
      field.blueprintId,
      field.nodeId,
      field.effects,
      field.scope.repeatedInstanceIdentifierContext.byBlueprintId,
      field.scope.repeatedInstanceIdentifierContext.byNodeId,
      onAnswerChange,
    ],
  );

  useEffect(() => {
    // Ignore the validity of hidden fields
    if (!field.visible) return;

    if (!field.valid) {
      onError?.(field.id, field.validationError?.message);
    }
  }, [field.id, field.valid, field.validationError?.message, field.visible, onError]);

  if (!field.visible) return null;

  const inputVariant: InputVariant = getInputVariant(styleVariant);
  const forceNewLine = shouldForceNewLine(field);
  const options = isRenderingOptionField(field) ? getVisibleOptions(field) : [];

  const placeholder = getPlaceholderForField(field);
  const disabled = field.readOnly || field.disabled;

  const fieldProps = {
    id: field.id,
    name: field.id,
    ['data-testid']: field.nodeId,
    label: field.label || '',
    onAnswerChange: onAnswerChangeForFieldProps,
    // TODO refactor `onAnswerComplete` to abstract the usage of `field.id`, similar to `onAnswerChange`
    onAnswerComplete,
    disabled,
    value: field.value,
    validationError: field.validationError,
    title: field.title || '',
    subtitle: field.text,
    required: !field.optional,
    locale,
    optionalText: translate('validation.optional', { locale }),
  };

  const fieldSize = getFieldSize(field);
  const imageSrc = field.info?.image && iconMap?.[field.info.image.name];
  const infoSupplementImage = imageSrc
    ? {
        src: imageSrc,
        alt: field.info?.image?.alt,
      }
    : undefined;

  const defaultContainerProps: FieldContainerProps = {
    forceNewLine,
    fieldSize,
    field,
    styleVariant,
    infoSupplementImage,
    onInfoIconClick,
    focusedIncompleteFieldId,
  };

  const isFocusedIncompleteField = focusedIncompleteFieldId === field.blueprintId;

  switch (field.type) {
    case FieldTypes.input:
      const symbol = isRenderingTextField(field) ? field.symbol : undefined;
      return (
        <FieldContainer {...defaultContainerProps}>
          <TextField
            {...fieldProps}
            boldedBorder={isFocusedIncompleteField}
            placeholder={placeholder}
            symbol={symbol}
            inputVariant={inputVariant}
            allowTextWrap={true}
          />
        </FieldContainer>
      );
    case FieldTypes.textarea:
      return (
        <FieldContainer {...defaultContainerProps}>
          <TextField
            {...fieldProps}
            placeholder={placeholder}
            multiline={true}
            minRows={3}
            maxRows={9}
            inputVariant={inputVariant}
            allowTextWrap={true}
            boldedBorder={isFocusedIncompleteField}
          />
        </FieldContainer>
      );
    case FieldTypes.number:
      return (
        <FieldContainer {...defaultContainerProps}>
          <NumberInput
            {...fieldProps}
            boldedBorder={isFocusedIncompleteField}
            placeholder={placeholder}
            inputVariant={inputVariant}
            numericalDataType={(field as RenderingNumberField).numericalDataType}
            allowTextWrap={true}
          />
        </FieldContainer>
      );
    case FieldTypes.money:
      return (
        <FieldContainer {...defaultContainerProps}>
          <MoneyInput
            {...fieldProps}
            boldedBorder={isFocusedIncompleteField}
            placeholder={placeholder}
            allowTextWrap={true}
          />
        </FieldContainer>
      );
    case FieldTypes.phone:
      return (
        <FieldContainer {...defaultContainerProps}>
          <PhoneField
            {...fieldProps}
            boldedBorder={isFocusedIncompleteField}
            saveOnBlur={true}
            placeholder={placeholder}
            inputVariant={inputVariant}
            allowTextWrap={true}
          />
        </FieldContainer>
      );
    case FieldTypes.radio:
      let radioOptionWidth: OptionWidth = 'full';

      if (isRenderingRadioField(field) && field.optionSize) {
        radioOptionWidth = field.optionSize;
      }

      return (
        <FieldContainer {...defaultContainerProps}>
          <RadioGroupField
            {...fieldProps}
            optionWidth={radioOptionWidth}
            iconMap={iconMap}
            options={options}
            displayOnlySelected={(field as RenderingOptionField).displayOnlySelected}
            styleVariant={styleVariant}
            onInfoIconClick={props.onInfoIconClick}
            renderingInfoSupplement={field.info}
            boldedBorder={isFocusedIncompleteField}
          />
        </FieldContainer>
      );
    case FieldTypes.checkbox:
      return (
        <FieldContainer {...defaultContainerProps}>
          <CheckboxField
            {...fieldProps}
            showError={!!fieldProps.validationError}
            label={fieldProps.label}
            styleVariant={styleVariant}
            boldedBorder={isFocusedIncompleteField}
          />
        </FieldContainer>
      );
    case FieldTypes.checkboxGroup:
      let checkboxOptionWidth: OptionWidth = 'full';
      if (isRenderingCheckboxGroupField(field) && field.optionSize) {
        checkboxOptionWidth = field.optionSize;
      }
      return (
        <FieldContainer {...defaultContainerProps}>
          <CheckboxGroup
            {...fieldProps}
            label={fieldProps.label}
            iconMap={iconMap}
            options={options}
            optionWidth={checkboxOptionWidth}
            styleVariant={styleVariant}
            onInfoIconClick={props.onInfoIconClick}
            boldedBorder={isFocusedIncompleteField}
          />
        </FieldContainer>
      );
    case FieldTypes.dropdown:
      if ((field as RenderingOptionField).searchable) {
        return (
          <FieldContainer {...defaultContainerProps}>
            <SearchableSelectField
              {...fieldProps}
              placeholder={placeholder ?? translate('select', { locale })}
              options={options}
              inputVariant={inputVariant}
              label={field.title}
              required={!field.optional}
              boldedBorder={isFocusedIncompleteField}
            />
          </FieldContainer>
        );
      }
      return (
        <FieldContainer {...defaultContainerProps}>
          <SelectField
            {...fieldProps}
            placeholder={placeholder ?? translate('select', { locale })}
            options={options}
            inputVariant={inputVariant}
            boldedBorder={isFocusedIncompleteField}
          />
        </FieldContainer>
      );

    case FieldTypes.date:
      return (
        <FieldContainer {...defaultContainerProps}>
          <DatePicker
            {...fieldProps}
            inputVariant={inputVariant}
            validationData={field?.validationData}
            boldedBorder={isFocusedIncompleteField}
          />
        </FieldContainer>
      );

    case FieldTypes.custom:
      if (isRenderingCustomField(field)) {
        switch (field.customName) {
          case 'participantID':
            return <ParticipantID {...fieldProps} />;
        }
      }
      break;

    case FieldTypes.yearMonth:
      return (
        <FieldContainer {...defaultContainerProps}>
          <YearMonthField {...fieldProps} inputVariant={inputVariant} boldedBorder={isFocusedIncompleteField} />
        </FieldContainer>
      );

    case FieldTypes.agree:
      if (styleVariant === StyleVariant.consumer) {
        if (isRenderingAgreeField(field)) {
          return (
            <FieldContainer {...defaultContainerProps}>
              <CheckboxAgree
                {...fieldProps}
                title={field.title || ''}
                showError={!!fieldProps.validationError}
                styleVariant={styleVariant}
                consentText={field.modalText || ''}
              />
            </FieldContainer>
          );
        }
      } else if (styleVariant === StyleVariant.pro) {
        if (isRenderingAgreeField(field)) {
          return (
            <FieldContainer {...defaultContainerProps}>
              <AgreeField
                {...fieldProps}
                text={field.text}
                title={field.title || ''}
                confirmedLabel={field.confirmedLabel}
                modalHeader={field.modalHeader}
                modalText={field.modalText}
                boldedBorder={isFocusedIncompleteField}
              />
            </FieldContainer>
          );
        }
      }
      break;

    case FieldTypes.button:
      if (isRenderingButtonField(field)) {
        return (
          <FieldContainer {...defaultContainerProps}>
            <ActionButton
              promptText={field.label || ''}
              linkText={field.buttonText || ''}
              onAnswerChange={fieldProps.onAnswerChange}
              resetOnMount={styleVariant === StyleVariant.consumer}
              icon={field.iconName ? <Icon name={IconNames[field.iconName as IconName]} size='20px' /> : undefined}
              boldedBorder={isFocusedIncompleteField}
              variant='button'
              disabled={fieldProps.disabled}
            />
          </FieldContainer>
        );
      }
      break;

    case FieldTypes.autocomplete:
      const { countryCode, nodeIdsToUpdate } = field as RenderingAutocompleteField;

      // onAnswerComplete is not defined on AddressAutoCompleteInput
      const { onAnswerComplete: __onAnswerComplete, ...addressAutoCompleteInputFieldProps } = fieldProps;

      return (
        <FieldContainer {...defaultContainerProps}>
          <AddressAutocompleteInput
            {...addressAutoCompleteInputFieldProps}
            boldedBorder={isFocusedIncompleteField}
            onAutocompleteAnswerChange={onAnswerChange}
            nodeIdScope={field.scope.repeatedInstanceIdentifierContext.byNodeId}
            countryCode={countryCode}
            nodeIdsToUpdate={nodeIdsToUpdate}
            inputVariant={inputVariant}
            placeholder={placeholder}
            variant='outlined'
          />
        </FieldContainer>
      );

    case FieldTypes.information:
    case FieldTypes.currencyCard:
      return (
        <FieldContainer {...defaultContainerProps}>
          <Fragment />
        </FieldContainer>
      );

    // This is handled by the dynamic PDF package, and we don't render this field type with the field-generator
    case FieldTypes.signature:
      return null;

    default:
      throw new Error('Please specify the type of field in FieldGenerator');
  }
  return <FieldContainer {...defaultContainerProps} />;
}

function getPlaceholderForField(field: RenderingField): string | undefined {
  return isRenderingPlaceholderField(field) ? field.placeholder : undefined;
}

export const Field = memo(InnerField);

interface FieldContainerProps {
  forceNewLine: boolean;
  fieldSize: FieldSize;
  field: RenderingField;
  styleVariant: StyleVariant;
  infoSupplementImage?: { src: string; alt?: string };
  onInfoIconClick?: () => void;
  children?: ReactNode;
  focusedIncompleteFieldId?: string;
  boldedBorder?: boolean;
  withoutInfoSupplement?: boolean;
}

function FieldContainer(props: FieldContainerProps): ReactElement {
  const {
    forceNewLine,
    field,
    children,
    fieldSize,
    styleVariant,
    onInfoIconClick,
    infoSupplementImage,
    focusedIncompleteFieldId,
    boldedBorder,
    withoutInfoSupplement = false,
  } = props;

  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusedIncompleteFieldId === field.blueprintId) {
      const { current } = divRef;
      if (current) {
        current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [focusedIncompleteFieldId, field.blueprintId]);

  const gridSize = useMemo(() => {
    if (withoutInfoSupplement) {
      return 12;
    }
    return (field.info ? (fieldSize as number) - INFO_ICON_BUTTON_SIZE : fieldSize) as GridSize;
  }, [withoutInfoSupplement, field.info, fieldSize]);

  return (
    <Fragment>
      {forceNewLine && <SpacerField styleVariant={styleVariant} />}

      <FieldGrid item xs={gridSize} data-testid={field.id} ref={divRef}>
        {children}
      </FieldGrid>
      {withoutInfoSupplement === false && field.info && (
        <CenteredAlignedGrid item xs={INFO_ICON_BUTTON_SIZE}>
          <Box mt={getTopMarginForInfoItem(styleVariant, field.type, !!field.label, !!field.text)}>
            <InfoSupplement
              title={field.info.title}
              text={field.info.text}
              image={infoSupplementImage}
              modalOptions={field.info.modalOptions}
              onClick={onInfoIconClick}
              boldedBorder={boldedBorder}
            />
          </Box>
        </CenteredAlignedGrid>
      )}
    </Fragment>
  );
}

function getTopMarginForInfoItem(
  styleVariant: StyleVariant,
  fieldType: FieldTypes,
  hasLabel: boolean,
  hasText: boolean,
): number {
  // The size of each field is consistent in the pro/lcp platform, meaning we can use the same margin value for all fields
  // But on the consumer platform, radios and checkboxes' height is much smaller than the height of the inputs, dropdowns etc.
  // TODO: Redesign this margin logic to not require these hardcoded values.
  if (styleVariant === StyleVariant.pro) return 4.2;
  if (fieldType === FieldTypes.checkbox) return 2;
  if (isTextFieldType(fieldType)) return 3;
  if (hasText) return 3;
  if (hasLabel) return 5;
  return 2;
}
